import { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang, getLocale, getCurrencySymbol } from "../lang/useLanguage";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const money = (v) =>
  v != null
    ? `${Number(v).toLocaleString(getLocale())} ${getCurrencySymbol()}`
    : "—";

const getMaintStatus = () => {
  const l = getLang();
  const m = l.maintenance_ || {};
  return {
    pending:     { label: m.statusPending,    color: "#fbbf24", bg: "rgba(251,191,36,0.12)"  },
    in_progress: { label: m.statusInProgress, color: "#60a5fa", bg: "rgba(96,165,250,0.12)"  },
    completed:   { label: m.statusCompleted,  color: "#00ff9d", bg: "rgba(0,255,157,0.12)"   },
    cancelled:   { label: m.statusCancelled,  color: "#f87171", bg: "rgba(248,113,113,0.12)" },
    overdue:     { label: m.statusOverdue,    color: "#f87171", bg: "rgba(248,113,113,0.12)" },
  };
};

const LIGHT_STATUS = {
  pending:     { color: "#d97706", bg: "#fffbeb" },
  in_progress: { color: "#2563eb", bg: "#eff6ff" },
  completed:   { color: "#059669", bg: "#ecfdf5" },
  cancelled:   { color: "#dc2626", bg: "#fef2f2" },
  overdue:     { color: "#dc2626", bg: "#fef2f2" },
};

const getMaintType = () => {
  const l = getLang();
  const m = l.maintenance_ || {};
  return {
    preventive:  { label: m.typePreventive,  icon: "🛡️", color: "#60a5fa" },
    corrective:  { label: m.typeCorrective,  icon: "🔧", color: "#f87171" },
    predictive:  { label: m.typePredictive,  icon: "🔮", color: "#a78bfa" },
    inspection:  { label: m.typeInspection,  icon: "🔍", color: "#fbbf24" },
    calibration: { label: m.typeCalibration, icon: "⚖️", color: "#34d399" },
  };
};

const getPriority = () => {
  const l = getLang();
  const m = l.maintenance_ || {};
  return {
    low:      { label: m.priorityLow,      color: "#64748b" },
    normal:   { label: m.priorityNormal,   color: "#60a5fa" },
    high:     { label: m.priorityHigh,     color: "#fbbf24" },
    critical: { label: m.priorityCritical, color: "#f87171" },
  };
};

const getFaultTypes = () => {
  const l = getLang();
  const m = l.maintenance_ || {};
  return [
    m.faultMechanical,
    m.faultElectrical,
    m.faultSoftware,
    m.faultVibration,
    m.faultOverheat,
    m.faultLeak,
    m.faultCalibration,
    m.faultSparePart,
    m.faultOther,
  ].filter(Boolean);
};

const sc = (status, themeName) => {
  if (themeName === "light") return LIGHT_STATUS[status] || LIGHT_STATUS.pending;
  return getMaintStatus()[status] || getMaintStatus().pending;
};

// ─── STATUS BADGE ─────────────────────────────────────────────────────────
const StatusBadge = ({ status, themeName }) => {
  const c = sc(status, themeName);
  const cfg = getMaintStatus()[status] || getMaintStatus().pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.color}44`,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.color }} />
      {cfg.label}
    </span>
  );
};

// ─── TAKVİM ───────────────────────────────────────────────────────────────
const MaintenanceCalendar = ({ records, t, themeName }) => {
  const { t: l } = useLanguage();
  const m = l.maintenance_ || {};
  const today = new Date();
  const days = [];
  for (let i = -3; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  const recordsOnDay = (day) => records.filter(r => {
    if (!r.scheduled_date) return false;
    return new Date(r.scheduled_date).toDateString() === day.toDateString();
  });
  return (
    <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>
        📅 {m.calendar}
      </div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8 }}>
        {days.map((day, i) => {
          const isToday = day.toDateString() === today.toDateString();
          const dayRecords = recordsOnDay(day);
          const hasCritical = dayRecords.some(r => r.priority === "critical");
          return (
            <div key={i} style={{ flexShrink: 0, width: 52, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ fontSize: 10, color: t.text.muted, fontWeight: 600 }}>
                {day.toLocaleDateString(getLocale(), { weekday: "short" })}
              </div>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: isToday ? t.accent.blue : dayRecords.length > 0 ? `${hasCritical ? t.accent.red : t.accent.yellow}22` : t.bg.tag,
                border: `2px solid ${isToday ? t.accent.blue : dayRecords.length > 0 ? (hasCritical ? t.accent.red : t.accent.yellow) : t.border.subtle}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700,
                color: isToday ? "#fff" : dayRecords.length > 0 ? (hasCritical ? t.accent.red : t.accent.yellow) : t.text.muted,
              }}>{day.getDate()}</div>
              {dayRecords.length > 0 && (
                <div style={{ fontSize: 10, color: hasCritical ? t.accent.red : t.accent.yellow, fontWeight: 700 }}>
                  {dayRecords.length} {m.maintenanceShort}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── DETAY MODAL ─────────────────────────────────────────────────────────
const MaintenanceDetailModal = ({ record, onClose, onEdit, onStatusChange, t, themeName }) => {
  const { t: l } = useLanguage();
  const m = l.maintenance_ || {};
  const [tab, setTab] = useState("overview");
  const [parts, setParts] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/maintenance/${record.id}/parts`, { headers: headers() });
        if (res.ok) setParts(await res.json());
      } catch {}
    };
    fetch_();
  }, [record.id]);

  const MAINT_STATUS = getMaintStatus();
  const MAINT_TYPE   = getMaintType();
  const PRIORITY     = getPriority();
  const nextStatus   = { pending: "in_progress", in_progress: "completed" };
  const next         = nextStatus[record.status];
  const typeCfg      = MAINT_TYPE[record.maintenance_type] || MAINT_TYPE.corrective;

  const handleStatus = async (s) => {
    setStatusLoading(true);
    try {
      const res = await fetch(`${API}/maintenance/${record.id}/status`, {
        method: "PATCH", headers: headers(), body: JSON.stringify({ status: s }),
      });
      if (res.ok) onStatusChange();
    } catch {}
    setStatusLoading(false);
  };

  const tabs = [
    { id: "overview", label: m.tabOverview },
    { id: "parts",    label: m.tabParts },
    { id: "cost",     label: m.tabCost },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 780, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>

        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              {typeCfg.icon}
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
                {record.title || `${m.maintenanceShort} #${record.id}`}
              </div>
              <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>
                {record.machine_name || record.machine_id || "—"} · {typeCfg.label}
              </div>
            </div>
            <StatusBadge status={record.status} themeName={themeName} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {next && (
              <button onClick={() => handleStatus(next)} disabled={statusLoading} style={{ padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", background: sc(next, themeName).color, color: "#fff", fontSize: 12, fontWeight: 700, opacity: statusLoading ? 0.6 : 1 }}>
                → {MAINT_STATUS[next]?.label}
              </button>
            )}
            <button onClick={() => onEdit(record)} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, cursor: "pointer", fontSize: 12 }}>✏️</button>
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 2, padding: "0 28px", borderBottom: `1px solid ${t.border.divider}`, background: t.bg.header }}>
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)} style={{ padding: "11px 14px", background: "none", border: "none", cursor: "pointer", color: tab === tb.id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${tab === tb.id ? t.accent.blue : "transparent"}`, transition: "all 0.2s" }}>{tb.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28, background: t.bg.modal }}>

          {tab === "overview" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  📋 {m.maintenanceInfo}
                </div>
                {[
                  [m.title_,           record.title],
                  [m.machine_,           record.machine_name || record.machine_id],
                  [m.type_,       typeCfg.label],
                  [m.priority_,          PRIORITY[record.priority]?.label || "—"],
                  [m.technician, record.technician_name || record.assigned_to],
                  [m.scheduledDate,  fmt(record.scheduled_date)],
                  [m.startedAt,        fmtDT(record.started_at)],
                  [m.completedAt,            fmtDT(record.completed_at)],
                  [m.estimatedHrs,     record.estimated_hours ? `${record.estimated_hours} ${m.hours}` : "—"],
                  [m.actualHrs,      record.actual_hours    ? `${record.actual_hours} ${m.hours}`    : "—"],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                    <span style={{ color: t.text.label }}>{k}</span>
                    <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    📝 {m.description_}
                  </div>
                  <div style={{ fontSize: 13, color: t.text.secondary, lineHeight: 1.7 }}>
                    {record.description || (m.noDescription)}
                  </div>
                </div>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    ✅ {m.workDone}
                  </div>
                  <div style={{ fontSize: 13, color: t.text.secondary, lineHeight: 1.7 }}>
                    {record.resolution || record.work_done || (m.notCompleted)}
                  </div>
                </div>
                {record.next_maintenance_date && (
                  <div style={{ background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 12, padding: "14px 18px" }}>
                    <div style={{ fontSize: 12, color: t.accent.blue, fontWeight: 700, marginBottom: 4 }}>
                      🔄 {m.nextMaintenance}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: t.text.primary }}>
                      {fmt(record.next_maintenance_date)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "parts" && (
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: t.bg.theadRow }}>
                    {[m.partName, m.stockCode, m.qty, m.unitPrice, m.total_].map(h => (
                      <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parts.length === 0
                    ? <tr><td colSpan={5} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{m.noParts}</td></tr>
                    : parts.map((p, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{p.name}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.muted }}>{p.stock_code || "—"}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{p.quantity} {p.unit}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{money(p.unit_price)}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 700, color: t.accent.blue, fontSize: 13 }}>{money((p.quantity || 0) * (p.unit_price || 0))}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "cost" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                {[
                  { label: m.laborCost, value: record.labor_cost, color: t.accent.blue },
                  { label: m.partsCost,   value: record.parts_cost, color: t.accent.yellow },
                  { label: m.totalCost_,   value: (record.labor_cost || 0) + (record.parts_cost || 0), color: t.accent.purple },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: t.bg.tag, borderRadius: 12, padding: "16px 18px", border: `1px solid ${t.border.subtle}` }}>
                    <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 6, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color }}>{money(value || 0)}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                {[
                  [m.estimatedCost, record.estimated_cost],
                  [m.actualCost,    (record.labor_cost || 0) + (record.parts_cost || 0)],
                  [m.variance_,              ((record.labor_cost || 0) + (record.parts_cost || 0)) - (record.estimated_cost || 0)],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                    <span style={{ color: t.text.label }}>{k}</span>
                    <span style={{ color: t.text.primary, fontWeight: 700 }}>{money(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── SERVİS TALEBİ DETAY MODAL ────────────────────────────────────────────
const ServiceDetailModal = ({ record, onClose, onEdit, onApprove, t, themeName }) => {
  const { t: l } = useLanguage();
  const m = l.maintenance_ || {};
  const PRIORITY = getPriority();
  const MAINT_STATUS = getMaintStatus();
  const [approveLoading, setApproveLoading] = useState(false);

  const handleApprove = async (action) => {
    setApproveLoading(true);
    try {
      const res = await fetch(`${API}/service/${record.id}/approve`, {
        method: "PATCH", headers: headers(), body: JSON.stringify({ action }),
      });
      if (res.ok) onApprove();
    } catch {}
    setApproveLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
              🔧 {m.serviceRequest} #{record.id}
            </div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>
              {record.machine_name || record.machine_id || "—"} · {fmtDT(record.created_at)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {record.status === "pending" && (
              <>
                <button onClick={() => handleApprove("approve")} disabled={approveLoading} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: t.accent.green, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                  ✅ {m.approve}
                </button>
                <button onClick={() => handleApprove("reject")} disabled={approveLoading} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 12 }}>
                  ✕ {m.reject}
                </button>
              </>
            )}
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
              {[
                [m.requester,       record.requester_name || record.created_by],
                [m.machine_,           record.machine_name || record.machine_id],
                [m.faultType_,       record.fault_type],
                [m.priority_,          PRIORITY[record.priority]?.label || "—"],
                [m.statusLabel,            MAINT_STATUS[record.status]?.label || record.status],
                [m.approvedBy,        record.approved_by || "—"],
                [m.approvedAt,      fmtDT(record.approved_at)],
                [m.estimatedCost,  money(record.estimated_cost)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                  <span style={{ color: t.text.label }}>{k}</span>
                  <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                </div>
              ))}
            </div>
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                📝 {m.description_}
              </div>
              <div style={{ fontSize: 13, color: t.text.secondary, lineHeight: 1.7 }}>
                {record.description || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── SERVİS TALEBİ FORM MODAL ─────────────────────────────────────────────
const ServiceRequestFormModal = ({ onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const m = l.maintenance_ || {};
  const PRIORITY = getPriority();
  const FAULT_TYPES = getFaultTypes();
  const [form, setForm] = useState({ machine_id: "", fault_type: "", priority: "normal", description: "", estimated_cost: "", contact_phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.machine_id || !form.description) { setError(m.errMachineDesc); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/service`, { method: "POST", headers: headers(), body: JSON.stringify({ ...form, status: "pending" }) });
      if (!res.ok) throw new Error((await res.json()).detail || (m.errGeneric));
      setSuccess(true);
      setTimeout(() => { onSaved(); }, 1500);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "10px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 560, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: "linear-gradient(135deg,rgba(248,113,113,0.15),rgba(239,68,68,0.08))", borderBottom: `1px solid ${t.accent.red}33`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
              🛠️ {m.createServiceRequest}
            </div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 3 }}>
              {m.serviceRequestNote}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 16 }}>
          {success ? (
            <div style={{ padding: "28px", textAlign: "center", background: t.accent.greenBg, border: `1px solid ${t.accent.green}44`, borderRadius: 14 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.accent.green, marginBottom: 6 }}>
                {m.requestSent}
              </div>
              <div style={{ fontSize: 13, color: t.text.muted }}>
                {m.requestSentNote}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  {m.machineId_} *
                </label>
                <input value={form.machine_id} onChange={e => set("machine_id", e.target.value)} placeholder={m.machineIdPlaceholder} style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  {m.faultType_}
                </label>
                <select value={form.fault_type} onChange={e => set("fault_type", e.target.value)} style={inputStyle}>
                  <option value="">{m.selectFaultType}</option>
                  {FAULT_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                  {m.priority_}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {Object.entries(PRIORITY).map(([k, v]) => (
                    <button key={k} onClick={() => set("priority", k)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, cursor: "pointer", border: `2px solid ${form.priority === k ? v.color : t.border.input}`, background: form.priority === k ? `${v.color}18` : t.bg.input, color: form.priority === k ? v.color : t.text.muted, fontSize: 12, fontWeight: 700, transition: "all 0.15s" }}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  {m.faultDescription} *
                </label>
                <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={4}
                  placeholder={m.faultDescPlaceholder}
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    {m.estimatedCostTl} ({getCurrencySymbol()})
                  </label>
                  <input type="number" value={form.estimated_cost} onChange={e => set("estimated_cost", e.target.value)} placeholder={m.optional} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    {m.contactInternal}
                  </label>
                  <input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder={m.internalNo} style={inputStyle} />
                </div>
              </div>
              <div style={{ padding: "12px 16px", background: t.accent.yellowBg, border: `1px solid ${t.accent.yellow}44`, borderRadius: 10, fontSize: 12, color: t.accent.yellow }}>
                ℹ️ {m.approvalFlow}
              </div>
              {error && <div style={{ padding: "10px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
            </>
          )}
        </div>

        {!success && (
          <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
            <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
              {m.cancel}
            </button>
            <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#f87171,#ef4444)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700, boxShadow: loading ? "none" : "0 4px 14px rgba(239,68,68,0.3)" }}>
              {loading ? (m.sending) : `🛠️ ${m.sendRequest}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── FORM MODAL ───────────────────────────────────────────────────────────
const FormModal = ({ data, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const m = l.maintenance_ || {};
  const MAINT_TYPE = getMaintType();
  const PRIORITY   = getPriority();
  const isEdit = !!data;
  const [form, setForm] = useState({
    title: data?.title || "",
    machine_id: data?.machine_id || "",
    maintenance_type: data?.maintenance_type || "preventive",
    priority: data?.priority || "normal",
    scheduled_date: data?.scheduled_date?.slice(0, 10) || "",
    assigned_to: data?.assigned_to || "",
    estimated_hours: data?.estimated_hours || "",
    estimated_cost: data?.estimated_cost || "",
    description: data?.description || "",
    next_maintenance_date: data?.next_maintenance_date?.slice(0, 10) || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title || !form.machine_id) { setError(m.errTitleMachine); return; }
    setLoading(true); setError("");
    try {
      const url = isEdit ? `${API}/maintenance/${data.id}` : `${API}/maintenance`;
      const res = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: headers(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (m.errGeneric));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "10px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };

  const fields = [
    { label: `${m.title_} *`, k: "title", full: true },
    { label: `${m.machineId_} *`, k: "machine_id" },
    { label: m.type_,              k: "maintenance_type", opts: Object.entries(MAINT_TYPE).map(([v, c]) => ({ v, l: c.label })) },
    { label: m.priority_,                  k: "priority",        opts: Object.entries(PRIORITY).map(([v, c]) => ({ v, l: c.label })) },
    { label: m.scheduledDate,          k: "scheduled_date",  type: "date" },
    { label: m.technician,         k: "assigned_to" },
    { label: m.estimatedHrsTl,     k: "estimated_hours", type: "number" },
    { label: `${m.estimatedCostTl} (${getCurrencySymbol()})`,    k: "estimated_cost",  type: "number" },
    { label: m.nextMaintDate,     k: "next_maintenance_date", type: "date" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
            {isEdit ? `✏️ ${m.editMaintenance}` : `➕ ${m.newMaintenance}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {fields.map(f => (
              <div key={f.k} style={{ gridColumn: f.full ? "1 / -1" : "auto" }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{f.label}</label>
                {f.opts ? (
                  <select value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={inputStyle}>
                    {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input type={f.type || "text"} value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={inputStyle} />
                )}
              </div>
            ))}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                {m.description_}
              </label>
              <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          {error && <div style={{ marginTop: 14, padding: "10px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
            {m.cancel}
          </button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600, boxShadow: loading ? "none" : t.shadow.btn }}>
            {loading ? (m.saving) : isEdit ? (m.update) : (m.plan)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── BAKIM KARTI ──────────────────────────────────────────────────────────
const MaintenanceCard = ({ record, onClick, t, themeName }) => {
  const { t: l } = useLanguage();
  const m = l.maintenance_ || {};
  const MAINT_TYPE = getMaintType();
  const PRIORITY   = getPriority();
  const c        = sc(record.status, themeName);
  const typeCfg  = MAINT_TYPE[record.maintenance_type] || MAINT_TYPE.corrective;
  const prCfg    = PRIORITY[record.priority] || PRIORITY.normal;
  const isOverdue = record.scheduled_date && new Date(record.scheduled_date) < new Date() && record.status === "pending";

  return (
    <div onClick={() => onClick(record)} style={{ background: t.bg.card, border: `1px solid ${isOverdue ? t.accent.red + "44" : t.border.card}`, borderRadius: 14, padding: 18, cursor: "pointer", transition: "all 0.2s", boxShadow: t.shadow.card, position: "relative", overflow: "hidden" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${c.color}55`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = isOverdue ? t.accent.red + "44" : t.border.card; }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${typeCfg.color}, transparent)`, borderRadius: "14px 14px 0 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>{typeCfg.icon}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>
              {record.title || `${m.maintenanceShort} #${record.id}`}
            </div>
            <div style={{ fontSize: 11, color: t.text.muted }}>{record.machine_name || record.machine_id || "—"}</div>
          </div>
        </div>
        <StatusBadge status={isOverdue ? "overdue" : record.status} themeName={themeName} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {[
          { label: m.type_,       value: typeCfg.label, color: typeCfg.color },
          { label: m.priority_,   value: prCfg.label,   color: prCfg.color   },
          { label: m.planned, value: fmt(record.scheduled_date), color: t.text.secondary },
          { label: m.technicianShort, value: record.technician_name || record.assigned_to || "—", color: t.text.secondary },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: t.bg.metricCard, borderRadius: 8, padding: "7px 10px", border: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, borderTop: `1px solid ${t.border.subtle}` }}>
        <span style={{ fontSize: 11, color: t.text.muted }}>⏱ {record.estimated_hours ? `${record.estimated_hours} ${m.hours}` : "—"}</span>
        <span style={{ fontSize: 11, color: t.accent.blue, fontWeight: 600 }}>{money(record.estimated_cost)}</span>
      </div>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function Maintenance() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const m = useMemo(() => l.maintenance_ || {}, [l.maintenance_]);
  const MAINT_STATUS = getMaintStatus();
  const MAINT_TYPE   = getMaintType();

  const [mainTab, setMainTab]         = useState(localStorage.getItem("tab_maintenance") || "maintenance");
  const [records, setRecords]         = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [search, setSearch]           = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType]   = useState("all");
  const [view, setView]               = useState(localStorage.getItem("view_maintenance") || "list");
  const [selected, setSelected]       = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editing, setEditing]         = useState(null);
  const [stats, setStats]             = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/maintenance`, { headers: headers() }),
        fetch(`${API}/service`,     { headers: headers() }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        const list = Array.isArray(data) ? data : data.items || [];
        setRecords(list);
        const pending   = list.filter(r => r.status === "pending").length;
        const overdue   = list.filter(r => r.scheduled_date && new Date(r.scheduled_date) < new Date() && r.status === "pending").length;
        const totalCost = list.filter(r => r.status === "completed").reduce((s, r) => s + ((r.labor_cost || 0) + (r.parts_cost || 0)), 0);
        setStats({ total: list.length, pending, overdue, totalCost });
      } else { throw new Error(m.errFetch); }
      if (r2.ok) {
        const data = await r2.json();
        setServiceRecords(Array.isArray(data) ? data : data.items || []);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [m]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = records.filter(r => {
    const matchSearch = !search ||
      (r.title || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.machine_id || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.machine_name || "").toLowerCase().includes(search.toLowerCase());
    const isOverdue = r.scheduled_date && new Date(r.scheduled_date) < new Date() && r.status === "pending";
    const effectiveStatus = isOverdue ? "overdue" : r.status;
    return matchSearch && (filterStatus === "all" || effectiveStatus === filterStatus) && (filterType === "all" || r.maintenance_type === filterType);
  });

  const filteredService = serviceRecords.filter(r =>
    !search || (r.machine_id || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const selectStyle = { padding: "10px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer" };
  const PRIORITY = getPriority();

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              🔧 {m.title}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {m.subtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
              🔄 {m.refresh}
            </button>
            <button onClick={() => setShowServiceForm(true)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#f87171,#ef4444)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 4px 14px rgba(239,68,68,0.3)" }}>
              🛠️ {m.serviceRequest}
            </button>
            {mainTab === "maintenance" && (
              <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: t.shadow.btn }}>
                + {m.planMaintenance}
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: m.statTotal, value: stats.total     || 0,   color: t.accent.blue,   icon: "🔧" },
            { label: m.statPending,     value: stats.pending   || 0,   color: t.accent.yellow, icon: "⏳" },
            { label: m.statOverdue,     value: stats.overdue   || 0,   color: t.accent.red,    icon: "⚠️" },
            { label: m.statCost, value: stats.totalCost ? `${Number(stats.totalCost / 1000).toLocaleString(getLocale(), { maximumFractionDigits: 0 })}K ${getCurrencySymbol()}` : `0 ${getCurrencySymbol()}`, color: t.accent.purple, icon: "💰" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 140, background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 24 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Gecikmiş uyarı */}
        {stats.overdue > 0 && (
          <div style={{ padding: "12px 20px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 10, color: t.accent.red, fontSize: 13, fontWeight: 600 }}>
            ⚠️ {stats.overdue} {m.overdueAlert}
          </div>
        )}

        {/* Takvim */}
        {mainTab === "maintenance" && records.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <MaintenanceCalendar records={records} t={t} themeName={themeName} />
          </div>
        )}

        {/* Ana Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 12, padding: 4, width: "fit-content" }}>
          {[
            ["maintenance", `🔧 ${m.tabMaintenance}`],
            ["service",     `🛠️ ${m.tabService}`],
          ].map(([v, label]) => (
            <button key={v} onClick={() => { setMainTab(v); localStorage.setItem("tab_maintenance", v); }} style={{ padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer", background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none", color: mainTab === v ? t.text.primary : t.text.muted, fontSize: 13, fontWeight: 600, boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s" }}>
              {label}
            </button>
          ))}
        </div>

        {/* BAKIM SEKMESİ */}
        {mainTab === "maintenance" && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
              <input placeholder={`🔍 ${m.searchPlaceholder}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                <option value="all">{m.allStatuses}</option>
                {Object.entries(MAINT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}>
                <option value="all">{m.allTypes}</option>
                {Object.entries(MAINT_TYPE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <div style={{ display: "flex", gap: 4, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
                {[["grid","⊞"],["list","≡"]].map(([v, icon]) => (
                  <button key={v} onClick={() => { setView(v); localStorage.setItem("view_maintenance", v); }} style={{ padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer", background: view === v ? t.bg.toggleActive : "none", color: view === v ? t.text.primary : t.text.muted, fontSize: 16 }}>{icon}</button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
                <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
                <span style={{ color: t.text.muted }}>{m.loading}</span>
              </div>
            ) : error ? (
              <div style={{ padding: 32, textAlign: "center", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 14, color: t.accent.red }}>
                ⚠️ {error}<br />
                <button onClick={fetchData} style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.accent.red}44`, background: "none", color: t.accent.red, cursor: "pointer" }}>
                  {m.retry}
                </button>
              </div>
            ) : view === "grid" ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
                {filtered.length === 0
                  ? <div style={{ gridColumn: "1/-1", textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                    {records.length === 0 ? (m.noRecords) : (m.noMatch)}
                  </div>
                  : filtered.map(r => <MaintenanceCard key={r.id} record={r} onClick={setSelected} t={t} themeName={themeName} />)}
              </div>
            ) : (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {[m.title_, m.machine_, m.type_, m.priority_, m.scheduledDate, m.technician, m.statusLabel, m.estimatedCost, m.actions].map(h => (
                        <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0
                      ? <tr><td colSpan={9} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{m.noMatch}</td></tr>
                      : filtered.map((r, i) => {
                        const typeCfg = MAINT_TYPE[r.maintenance_type] || MAINT_TYPE.corrective;
                        const isOverdue = r.scheduled_date && new Date(r.scheduled_date) < new Date() && r.status === "pending";
                        return (
                          <tr key={r.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                            <td style={{ padding: "12px 14px", fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{r.title || `#${r.id}`}</td>
                            <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{r.machine_name || r.machine_id || "—"}</td>
                            <td style={{ padding: "12px 14px" }}><span style={{ color: typeCfg.color, fontSize: 12, fontWeight: 600 }}>{typeCfg.icon} {typeCfg.label}</span></td>
                            <td style={{ padding: "12px 14px" }}><span style={{ color: PRIORITY[r.priority]?.color || t.text.muted, fontSize: 12, fontWeight: 600 }}>{PRIORITY[r.priority]?.label || "—"}</span></td>
                            <td style={{ padding: "12px 14px", fontSize: 12, color: isOverdue ? t.accent.red : t.text.muted, fontWeight: isOverdue ? 700 : 400 }}>{fmt(r.scheduled_date)}</td>
                            <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{r.technician_name || r.assigned_to || "—"}</td>
                            <td style={{ padding: "12px 14px" }}><StatusBadge status={isOverdue ? "overdue" : r.status} themeName={themeName} /></td>
                            <td style={{ padding: "12px 14px", fontSize: 13, color: t.accent.blue }}>{money(r.estimated_cost)}</td>
                            <td style={{ padding: "12px 14px" }}>
                              <div style={{ display: "flex", gap: 6 }}>
                                <button onClick={() => setSelected(r)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue }}>{m.detail}</button>
                                <button onClick={() => { setEditing(r); setShowForm(true); }} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.bg.toggle, border: `1px solid ${t.border.input}`, color: t.text.secondary }}>✏️</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* SERVİS TALEPLERİ SEKMESİ */}
        {mainTab === "service" && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <input placeholder={`🔍 ${m.searchServicePlaceholder}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1 }} />
            </div>
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
                <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
                <span style={{ color: t.text.muted }}>{m.loading}</span>
              </div>
            ) : (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {["#", m.machine_, m.faultType_, m.requester, m.priority_, m.date_, m.estimatedCost, m.statusLabel, m.actions].map(h => (
                        <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredService.length === 0
                      ? <tr><td colSpan={9} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>
                        {serviceRecords.length === 0 ? (m.noServiceRecords) : (m.noMatch)}
                      </td></tr>
                      : filteredService.map((r, i) => (
                        <tr key={r.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding: "12px 14px", fontWeight: 700, color: t.accent.blue, fontSize: 13 }}>#{r.id}</td>
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{r.machine_name || r.machine_id || "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 13, color: t.text.secondary }}>{r.fault_type || "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{r.requester_name || r.created_by || "—"}</td>
                          <td style={{ padding: "12px 14px" }}><span style={{ color: PRIORITY[r.priority]?.color || t.text.muted, fontSize: 12, fontWeight: 600 }}>{PRIORITY[r.priority]?.label || "—"}</span></td>
                          <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{fmtDT(r.created_at)}</td>
                          <td style={{ padding: "12px 14px", fontSize: 13, color: t.accent.blue }}>{money(r.estimated_cost)}</td>
                          <td style={{ padding: "12px 14px" }}><StatusBadge status={r.status} themeName={themeName} /></td>
                          <td style={{ padding: "12px 14px" }}>
                            <button onClick={() => setSelectedService(r)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue }}>{m.detail}</button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {!loading && !error && (
          <div style={{ marginTop: 16, color: t.text.placeholder, fontSize: 12, textAlign: "right" }}>
            {mainTab === "maintenance"
              ? `${filtered.length} / ${records.length} ${m.recordCount}`
              : `${filteredService.length} ${m.serviceCount}`}
          </div>
        )}
      </div>

      {showServiceForm && (
        <ServiceRequestFormModal t={t}
          onClose={() => setShowServiceForm(false)}
          onSaved={() => { setShowServiceForm(false); fetchData(); setMainTab("service"); localStorage.setItem("tab_maintenance", "service"); }} />
      )}
      {selected && (
        <MaintenanceDetailModal record={selected} t={t} themeName={themeName}
          onClose={() => setSelected(null)}
          onEdit={(r) => { setEditing(r); setShowForm(true); setSelected(null); }}
          onStatusChange={() => { fetchData(); setSelected(null); }} />
      )}
      {selectedService && (
        <ServiceDetailModal record={selectedService} t={t} themeName={themeName}
          onClose={() => setSelectedService(null)}
          onEdit={(r) => { setSelectedService(null); }}
          onApprove={() => { fetchData(); setSelectedService(null); }} />
      )}
      {showForm && (
        <FormModal data={editing} t={t}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchData(); }} />
      )}
    </div>
  );
}
